import {type ReactNode, useMemo, useState} from 'react'

import {useAddDestinationAction} from './AddDestination/useAddDestinationAction'
import {useOrderAction} from './Order'
import {getTriggerOpenMapActionHandler, RouteMap} from './RouteMap'
import {getSelectDueAction, SelectDueAction} from './SelectDue'
import {type ActionComponentProps, type ActionConfig, ActionType} from './types'

export const useAction = <T extends ActionConfig>(
  props?: T,
  actionComponentProps?: Pick<ActionComponentProps, 'onChange'>,
): {ActionComponent: ReactNode; triggerAction: VoidFunction | undefined} => {
  const [isActive, setActive] = useState(false)

  const triggerAddDestinationAction = useAddDestinationAction()
  const triggerOrderAction = useOrderAction()

  const [ActionWidget, triggerAction] = useMemo<[ReactNode, VoidFunction | undefined]>(() => {
    if (!props) {
      return [null, void 0]
    }

    if (!props?.type) {
      throw new Error('Action type is not found')
    }

    switch (props.type) {
      case ActionType.ADD_DESTINATION:
        return [null, triggerAddDestinationAction]

      case ActionType.OPEN_MAP:
        return [
          isActive ? <RouteMap onClose={() => setActive(false)} /> : null,
          getTriggerOpenMapActionHandler(setActive),
        ]

      case ActionType.ORDER:
        return [null, triggerOrderAction]

      case ActionType.SELECT_DUE:
        return [
          <SelectDueAction {...props} {...actionComponentProps} isActive={isActive} setActive={setActive} />,
          getSelectDueAction(setActive),
        ]

      default:
        return [null, void 0]
    }
  }, [actionComponentProps, isActive, props, triggerAddDestinationAction, triggerOrderAction])

  return {
    ActionComponent: ActionWidget,
    triggerAction,
  }
}
